import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal } from 'features/modal/modalSlice';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { clearContentRequested } from 'features/application/actions';
import {
  selectBreakoutRoomsStatus,
  selectIsBreakoutRoom,
} from 'features/breakout-rooms/breakoutRoomsSlice';

const ClearContentLibraryConfirmation = () => {
  const { t } = useTranslation(['modals', 'settings']);
  const dispatch = useAppDispatch();

  const { hasPermissions } = usePermissions();
  const isBreakout = useAppSelector(selectIsBreakoutRoom);
  const breakoutRoomsStatus = useAppSelector(selectBreakoutRoomsStatus);

  const [roomLibrary, setRoomLibrary] = useState(false);
  const [sharedFiles, setSharedFiles] = useState(false);

  const canClearContent =
    hasPermissions(PermissionTypes.clearContent) &&
    !isBreakout &&
    breakoutRoomsStatus !== 'started';

  useEffect(() => {
    if (!canClearContent) {
      dispatch(closeModal('clearContentLibraryConfirmation'));
    }
  }, [canClearContent, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('clearContentLibraryConfirmation'));
  };

  const handleOk = () => {
    dispatch(
      clearContentRequested({
        publicChat: false,
        roomLibrary,
        sharedFiles,
        whiteboards: false,
        sharedNotes: false,
        qa: false,
        pollResults: false,
        quizResults: false,
      })
    );
    dispatch(closeModal('clearContentLibraryConfirmation'));
  };

  return (
    <ConfirmationDialog
      open={canClearContent}
      title={t('clear_content_library_confirmation.title')}
      description={t('clear_content_library_confirmation.description')}
      cancelText={t('clear_content_library_confirmation.btn_cancel_label')}
      okText={t('clear_content_library_confirmation.btn_ok_label')}
      onCancel={handleCancel}
      onOk={handleOk}
      hideCloseButton
      okButtonProps={{ color: 'error', disabled: !roomLibrary && !sharedFiles }}
    >
      <Box sx={{ mt: 2 }}>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={roomLibrary}
                name="roomLibrary"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setRoomLibrary(event.target.checked)
                }
              />
            }
            label={t('settings:clear_content.content_types.public_room_files')}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={sharedFiles}
                name="sharedFiles"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSharedFiles(event.target.checked)
                }
              />
            }
            label={t('settings:clear_content.content_types.shared_files')}
          />
        </div>
      </Box>
    </ConfirmationDialog>
  );
};

export default ClearContentLibraryConfirmation;
